/**
 * PhaseReconciliationPanel — B-2026-0033 §D.
 *
 * Aligns the coaching phases the coach spoke in the teaching video to the
 * detected/edited template phases, so accepted coaching goals scope to the
 * correct phase. Downstream, the backend MATERIALISER matches a suggestion's
 * `target_phase` (a coaching phase name) to a template phase by normalized
 * name — so once the template phase names equal the coaching phase names, that
 * match is guaranteed, instead of the check falling back to whole-video with a
 * `phase_not_matched` notice. (That matching used to live in this client too,
 * in `reconcilePhase()`; PR-3 moved it to the one converter. The comparison
 * THIS panel does is a display comparison and stays here, using
 * `utils/phaseNameMatch.normalizePhaseName` — the same normalisation the
 * backend applies.)
 *
 * Reconcile BEFORE accepting checks: the panel is rendered above the suggestion
 * list in CreateTemplate for exactly that reason. On the live 30-suggestion run
 * that motivated PR-3, 28 of 30 came back scoped to the whole video because
 * their phase names did not match (audit item E2E-2).
 *
 * Owner decision D8 (2026-10-07): phase NAMES are reconciled; frame boundaries
 * are never proposed. The coach sets start and end on the timeline.
 *
 * Two modes (Finding 7 / acceptance criteria):
 *   §D-1  counts match  → one-click "Apply coaching phase names" (in order).
 *   §D-2  counts differ → a mapping UI; the coach maps each coaching phase to a
 *                         template phase. Nothing is applied until the coach
 *                         clicks Apply — never a silent misattribution.
 *
 * CREATION-TIME ONLY — no eval/scoring path is touched. This renames template
 * phase NAMES only; `id`, `start_frame`, `end_frame` are preserved, so phase
 * scoring configs and speed intents (both keyed by `phase.id`) are never
 * orphaned. Score-neutral by construction.
 */

import { useMemo, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { useTeachingVideoResult } from '@/hooks/useTeachingVideo';
import { normalizePhaseName } from '@/utils/phaseNameMatch';
import { Card, CardContent, CardHeader, CardTitle, Alert, Button } from '@/components/ui';
import Badge from '@/components/ui/Badge';
import type { Phase } from '@/types/api';
import type { CoachingPhase } from '@/types/teachingVideo';

interface PhaseReconciliationPanelProps {
  /** Teaching video whose coaching phases we reconcile against. */
  teachingVideoId: string;
  /** The current template phases (manualPhases) — has `.name`, `.id`, frames. */
  templatePhases: Phase[];
  /**
   * Called with a new phase list when the coach applies names. Only the `name`
   * field changes; all other fields (id, frames) are preserved. The parent
   * replaces its `manualPhases` state with this list.
   */
  onApplyNames: (updated: Phase[]) => void;
}

const SKIP = '' as const;

export default function PhaseReconciliationPanel({
  teachingVideoId,
  templatePhases,
  onApplyNames,
}: PhaseReconciliationPanelProps) {
  const { data } = useTeachingVideoResult(teachingVideoId);

  const coachingPhases: CoachingPhase[] = useMemo(
    () => (data?.status === 'completed' ? (data.result?.analysis?.phases ?? []) : []),
    [data],
  );

  const countsMatch =
    coachingPhases.length > 0 && coachingPhases.length === templatePhases.length;

  // Already aligned (counts match AND in-order normalized names all equal) —
  // nothing to reconcile; show a quiet confirmation instead of nagging.
  const alreadyAligned = useMemo(
    () =>
      countsMatch &&
      coachingPhases.every(
        (cp, i) => normalizePhaseName(cp.name) === normalizePhaseName(templatePhases[i].name),
      ),
    [countsMatch, coachingPhases, templatePhases],
  );

  // §D-2 mapping state: coaching phase index → chosen template phase id (or SKIP).
  const [mapping, setMapping] = useState<Record<number, string>>({});
  const [justApplied, setJustApplied] = useState(false);

  // Nothing to reconcile: no coaching phases (video absent/processing/failed)
  // or no template phases defined yet.
  if (coachingPhases.length === 0 || templatePhases.length === 0) {
    return null;
  }

  if (alreadyAligned) {
    return (
      <Alert variant="success">
        <span className="inline-flex items-center gap-1.5 text-sm">
          <CheckIcon className="h-4 w-4 flex-shrink-0" />
          Template phases are aligned with the coaching video.
        </span>
      </Alert>
    );
  }

  const coachingNames = coachingPhases.map((p) => p.name).join(', ');
  const templateNames = templatePhases.map((p) => p.name).join(', ');

  // --- §D-1: counts match → apply coaching names in order ---
  const applyInOrder = () => {
    const updated = templatePhases.map((p, i) => ({ ...p, name: coachingPhases[i].name }));
    onApplyNames(updated);
    setJustApplied(true);
  };

  // --- §D-2: counts differ → apply the coach-authored mapping ---
  // Each template phase that some coaching phase maps to is renamed to that
  // coaching phase's name; unmapped template phases keep their existing name.
  const hasMapping = Object.values(mapping).some((tid) => tid !== SKIP);
  const applyMapping = () => {
    const updated = templatePhases.map((p) => {
      const coachEntry = Object.entries(mapping).find(([, tid]) => tid === p.id);
      if (coachEntry) {
        const coachIdx = Number(coachEntry[0]);
        return { ...p, name: coachingPhases[coachIdx].name };
      }
      return p;
    });
    onApplyNames(updated);
    setJustApplied(true);
  };

  const setMappingFor = (coachIdx: number, templateId: string) => {
    setJustApplied(false);
    setMapping((prev) => ({ ...prev, [coachIdx]: templateId }));
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Align coaching phases</CardTitle>
        <p className="text-sm text-text-secondary mt-1">
          Match the phases the coach described to your template phases so coaching
          goals scope to the right phase instead of the whole video.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-3">
            <p className="text-xs font-medium text-text-muted mb-1">
              Coaching video ({coachingPhases.length})
            </p>
            <p className="text-sm text-text-secondary">{coachingNames}</p>
          </div>
          <div className="rounded-lg border border-border-subtle bg-dark-elevated-2 p-3">
            <p className="text-xs font-medium text-text-muted mb-1">
              Template ({templatePhases.length})
            </p>
            <p className="text-sm text-text-secondary">{templateNames}</p>
          </div>
        </div>

        {countsMatch ? (
          // §D-1 — same count: a single in-order apply is unambiguous.
          <div className="flex flex-wrap items-center gap-3">
            <Button variant="primary" size="sm" onClick={applyInOrder}>
              Apply coaching phase names
            </Button>
            <span className="text-xs text-text-muted">
              Renames your {templatePhases.length} phases in order to the coaching names.
            </span>
          </div>
        ) : (
          // §D-2 — different counts: coach maps each coaching phase explicitly.
          <div className="space-y-3">
            <Alert variant="warning">
              <span className="text-xs">
                The coaching video describes {coachingPhases.length}{' '}
                {coachingPhases.length === 1 ? 'phase' : 'phases'} but your template has{' '}
                {templatePhases.length}. Map each coaching phase to a template phase
                (optional). Unmapped phases keep their current name.
              </span>
            </Alert>
            <div className="space-y-2">
              {coachingPhases.map((cp, coachIdx) => {
                // Template ids already chosen by a DIFFERENT coaching phase — a
                // template phase can host only one coaching name, so disable
                // those options to prevent an accidental double-map.
                const takenByOthers = new Set(
                  Object.entries(mapping)
                    .filter(([ci]) => Number(ci) !== coachIdx)
                    .map(([, tid]) => tid)
                    .filter((tid) => tid !== SKIP),
                );
                return (
                  <div key={coachIdx} className="flex items-center gap-3">
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span className="truncate text-sm text-text-primary">{cp.name}</span>
                      {cp.approximate_duration_hint && (
                        <Badge variant="default" size="sm">
                          ~{cp.approximate_duration_hint}
                        </Badge>
                      )}
                    </div>
                    <span className="text-text-muted" aria-hidden>
                      →
                    </span>
                    <select
                      className="w-40 rounded-md border border-border-subtle bg-dark-elevated-2 px-2 py-1 text-sm text-text-primary"
                      value={mapping[coachIdx] ?? SKIP}
                      onChange={(e) => setMappingFor(coachIdx, e.target.value)}
                      aria-label={`Template phase for coaching phase ${cp.name}`}
                    >
                      <option value={SKIP}>— skip —</option>
                      {templatePhases.map((tp) => (
                        <option
                          key={tp.id}
                          value={tp.id}
                          disabled={takenByOthers.has(tp.id)}
                        >
                          {tp.name}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="primary"
                size="sm"
                onClick={applyMapping}
                disabled={!hasMapping}
              >
                Apply mapping
              </Button>
              <span className="text-xs text-text-muted">
                Renames only the mapped template phases.
              </span>
            </div>
          </div>
        )}

        {justApplied && (
          <p className="inline-flex items-center gap-1.5 text-xs text-success">
            <CheckIcon className="h-4 w-4 flex-shrink-0" />
            Applied — coaching goals will now scope to these phases.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
